/**
 * Single source of truth for database provider → SQL dialect resolution.
 *
 * Both the compiler (`apps/compiler/src/transforms/dialect.ts`) and the CLI
 * (`quickback schema check|sync`) resolve dialects through this module. Keeping
 * one definition here is what prevents the class of bug where the two sides
 * disagree and `schema sync` writes wrong-dialect declarations into authored
 * source (issue #213).
 */

export type ProviderDialect = 'sqlite' | 'postgres' | 'mysql';

export const SQLITE_PROVIDERS = [
  'cloudflare-d1',
  'better-sqlite3',
  'bun-sqlite',
  'libsql',
  'turso',
] as const;

export const POSTGRES_PROVIDERS = [
  'supabase',
  'neon',
  'vercel-postgres',
  'postgres',
  'postgresql',
] as const;

export const MYSQL_PROVIDERS = ['planetscale', 'mysql', 'mysql2'] as const;

/**
 * Resolve a database provider name to its SQL dialect family.
 *
 * Returns `undefined` for unknown or missing provider names so each caller
 * chooses its own fallback explicitly (the compiler defaults to sqlite; the
 * CLI does the same but refuses dialects it cannot emit).
 */
export function resolveProviderDialect(
  providerName: string | undefined,
): ProviderDialect | undefined {
  if (!providerName) return undefined;
  if ((SQLITE_PROVIDERS as readonly string[]).includes(providerName)) return 'sqlite';
  if ((POSTGRES_PROVIDERS as readonly string[]).includes(providerName)) return 'postgres';
  if ((MYSQL_PROVIDERS as readonly string[]).includes(providerName)) return 'mysql';
  return undefined;
}
